import {
  Combobox,
  type ComboboxProps,
  type SingleComboboxOption,
  cn,
  useCombobox,
} from '@sr/ui-library';
import type { FilterOption } from '../pages/v3/Title/titleFilterOptions';
import type { TitleFilter } from '../pages/v3/Title/useTitleFilters';

/** Shown for a client or claimant the filter options came back without a name for. */
export const UNNAMED_CLIENT_LABEL = 'Unnamed client';

/**
 * Keeps a stale selection (not in the options) visible and clearable, and labels
 * options the API couldn't name via `toLabel`.
 */
export const withSelectedOptions = (
  options: (Omit<FilterOption, 'label'> & { label?: string })[],
  selected: string[],
  toLabel: (value: string) => string,
): FilterOption[] => {
  const known = new Set(options.map((option) => option.value));
  const missing = selected.filter((value) => !known.has(value));

  return [
    ...options.map((option) => ({
      ...option,
      label: option.label || toLabel(option.value),
    })),
    ...missing.map((value) => ({ label: toLabel(value), value })),
  ];
};

type FilterComboboxProps = Omit<
  ComboboxProps,
  'options' | 'store' | 'trigger'
> & {
  className?: string;
  disabled: boolean;
  filter: TitleFilter;
  hasSearch?: boolean;
  /** Wider than `FilterOption` so a caller can pass a non-selectable row. */
  options: SingleComboboxOption[];
  /** Shown on the trigger once more than one option is selected. */
  selectedPlaceholder: string;
  sorting?: 'asc' | 'desc' | 'none';
};

/**
 * A `Combobox` for one URL-backed filter. Rest props reach the trigger (as in
 * `CountryCombobox`) so an `aria-label` survives the placeholder being replaced.
 */
export const FilterCombobox = ({
  className = '',
  disabled,
  filter,
  hasSearch = false,
  options,
  popoverProps,
  selectedPlaceholder,
  sorting,
  ...props
}: FilterComboboxProps) => {
  const store = useCombobox({
    hasSearch,
    options,
    selected: filter.selected,
    setSelected: filter.setSelected,
    sorting,
  });

  return (
    // Fieldset, not `disabled`: dodges index.css's grey `button[disabled]` rule.
    // Kept enabled while selected so Clear still works.
    <fieldset
      className={cn(
        'min-w-0 rounded-md bg-card disabled:cursor-not-allowed sm:min-w-44 sm:flex-1',
        className,
      )}
      disabled={disabled && filter.selected.length === 0}
    >
      <Combobox
        store={store}
        // Rest props first: `aria-label` and `placeholder` forward to the trigger,
        // while the props below are this component's own contract.
        {...props}
        multipleSelectedPlaceholder={selectedPlaceholder}
        popoverProps={popoverProps}
      />
    </fieldset>
  );
};
